import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";

import { itineraryPdfRuntime } from "../runtime";
import { ItineraryPdfDocument } from "../ui/ItineraryPdfDocument";

/** Python Chromium 专用的后台行程渲染页，不挂载后台壳层。 */
export function ItineraryPdfRenderPage() {
  const { itineraryId = "" } = useParams();
  const query = useQuery({
    queryKey: ["itinerary", "pdf-render", itineraryId],
    enabled: Boolean(itineraryId),
    retry: false,
    queryFn: () => itineraryPdfRuntime.loadPrivate(itineraryId),
  });
  return <PdfRenderResult itinerary={query.data} failed={!itineraryId || query.isError} />;
}

/** Python Chromium 专用的公开分享渲染页。 */
export function ItineraryPublicPdfRenderPage() {
  const { token = "" } = useParams();
  const query = useQuery({
    queryKey: ["itinerary", "public", "pdf-render", token],
    enabled: Boolean(token),
    retry: false,
    queryFn: () => itineraryPdfRuntime.loadPublic(token),
  });
  return <PdfRenderResult itinerary={query.data} failed={!token || query.isError} publicShare />;
}

/** 把异步数据状态投影为 Python 可观察的页面状态。 */
function PdfRenderResult({ itinerary, failed, publicShare = false }: {
  itinerary?: Awaited<ReturnType<typeof itineraryPdfRuntime.loadPrivate>>;
  failed: boolean;
  publicShare?: boolean;
}) {
  const [searchParams] = useSearchParams();
  const includeDayImages = searchParams.get("include_day_images") !== "false";
  const state = failed ? "error" : itinerary ? "ready" : "loading";
  useEffect(() => {
    const previousTitle = document.title;
    document.documentElement.dataset.itineraryPdfState = state;
    document.documentElement.classList.add("itinerary-pdf-render-root");
    if (publicShare) document.title = "花儿艺术旅行";
    else if (itinerary?.title) document.title = `${itinerary.title} - 行程 PDF`;
    return () => {
      document.title = previousTitle;
      delete document.documentElement.dataset.itineraryPdfState;
      document.documentElement.classList.remove("itinerary-pdf-render-root");
    };
  }, [itinerary?.title, publicShare, state]);

  if (failed) return <main className="itinerary-pdf-render-state">行程 PDF 渲染失败</main>;
  if (!itinerary) return <main className="itinerary-pdf-render-state">正在准备行程 PDF...</main>;
  return <main className="itinerary-pdf-render-page"><ItineraryPdfDocument itinerary={itinerary} includeDayImages={includeDayImages} /></main>;
}
